import type { ReactNode } from 'react'

import { Flex } from 'antd'
import { Checkbox, Link, T } from 'govwell-ui'
import isNil from 'lodash.isnil'
import { computed, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import { ViewFilterOptionFragment } from 'types/graphql'

import EmptyState from 'src/components/EmptyState'
import RecordTagsFilter from 'src/components/TableViews/RecordTagsFilter'
import DateFilter from 'src/components/TableViews/DateFilter'
import FilterTypeSelect from 'src/components/TableViews/FilterTypeSelect'
import NumberFilter from 'src/components/TableViews/NumberFilter'
import OptionCheckbox from 'src/components/TableViews/OptionCheckbox'
import { useTableViewConfig } from 'src/components/TableViews/TableViewConfigContext'
import Text from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'
import { TableColumnTemplate } from 'src/models/TableViews/TableColumnTemplate'
import { recordTypeStatusOptionsSpanMultipleModules } from 'src/models/TableViews/TableViewFilter'

import { useMyOrg } from '../../layouts/AppStateContextLayout/utils'

type ModuleGroup = {
  recordTypeId: number | null
  moduleLabel: string
  options: ViewFilterOptionFragment[]
}

/**
 * Splits RecordTypeStatus filter options into per-module sections (caller
 * should only invoke when options span 2+ modules). Section headers show the
 * module name; options under each header are status names only. Both modules
 * and statuses within each module sort alphabetically by name.
 */
const groupOptionsByModule = (options: ViewFilterOptionFragment[]): ModuleGroup[] => {
  const groupsByKey = new Map<string, ModuleGroup>()
  for (const option of options) {
    if (option.data.__typename !== 'ViewDataElementRecordTypeStatus') {
      continue
    }
    const recordType = option.data.recordTypeStatus?.recordType
    const recordTypeId = recordType?.id ?? null
    const moduleLabel = recordType?.displayName ?? 'Other'
    const key = recordTypeId !== null ? `id:${recordTypeId}` : `label:${moduleLabel}`
    let group = groupsByKey.get(key)
    if (!group) {
      group = { recordTypeId, moduleLabel, options: [] }
      groupsByKey.set(key, group)
    }
    group.options.push(option)
  }
  const groups = Array.from(groupsByKey.values()).sort((a, b) =>
    a.moduleLabel.localeCompare(b.moduleLabel)
  )
  for (const group of groups) {
    group.options.sort((a, b) => {
      const aName =
        a.data.__typename === 'ViewDataElementRecordTypeStatus'
          ? (a.data.recordTypeStatus?.name ?? '')
          : ''
      const bName =
        b.data.__typename === 'ViewDataElementRecordTypeStatus'
          ? (b.data.recordTypeStatus?.name ?? '')
          : ''
      return aName.localeCompare(bName)
    })
  }
  return groups
}

const renderGroupedSections = (
  options: ViewFilterOptionFragment[],
  renderOption: (option: ViewFilterOptionFragment) => ReactNode,
  { applyFirstGroupMargin }: { applyFirstGroupMargin: boolean }
) => {
  const groups = groupOptionsByModule(options)
  return groups.map((group, idx) => (
    <Flex key={group.recordTypeId ?? group.moduleLabel} vertical gap="6px">
      <T.BodySmall
        className={applyFirstGroupMargin && idx > 0 ? 'mt-2 text-neutral-600' : 'text-neutral-600'}
      >
        {group.moduleLabel}
      </T.BodySmall>
      {group.options.map(renderOption)}
    </Flex>
  ))
}

const StyledFlex = styled(Flex)`
  padding: 0px 12px;
`

type Props = {
  columnTemplate: TableColumnTemplate
}
const FilterEditor = ({ columnTemplate }: Props) => {
  const { filterManager } = useTableViewConfig()
  const paymentProviderFormatted = useMyOrg()?.paymentProviderFormatted || ''
  const { isOpen: isArchivedOptionsListOpen, open: openArchivedOptionsList } = useDisclosure()

  // Mirrors AddFilterButton's guard. The two are only ever invoked from
  // chip rows or the add-filter dropdown, both of which already gate on
  // `filterManager` being non-null at the parent — but we re-check here
  // so a future caller can't render a `FilterEditor` outside a
  // configured provider scope.
  if (!filterManager) {
    return null
  }
  const filter = filterManager.getFilterByColumnGeneratedId(columnTemplate.generatedId)

  const renderInputs = () => {
    switch (columnTemplate.resolvedDataType ?? columnTemplate.dataType) {
      case 'Boolean':
        return (
          <>
            <Checkbox
              value={filter.bool === true}
              onValueChange={(val) => {
                runInAction(() => {
                  if (!val) {
                    filter.bool = undefined
                  }
                  if (val) {
                    filter.bool = true
                  }
                })
              }}
            >
              True
            </Checkbox>
            <Checkbox
              value={filter.bool === false}
              onValueChange={(val) => {
                runInAction(() => {
                  if (!val) {
                    filter.bool = undefined
                  }
                  if (val) {
                    filter.bool = false
                  }
                })
              }}
            >
              False
            </Checkbox>
          </>
        )
      case 'Number':
      case 'CurrencyCents':
        return <NumberFilter filter={filter} />
      case 'Date':
      case 'DateTime':
        return <DateFilter filter={filter} />
      default: {
        if (columnTemplate.isDateFieldColumn) {
          return <DateFilter filter={filter} />
        }
        if (columnTemplate.columnType === 'RecordTags') {
          if (!isNil(filter.isNull)) {
            return null
          }
          return (
            <RecordTagsFilter
              filter={filter}
              placeholder={`Search ${columnTemplate.labelSentenceCase.toLowerCase()}...`}
            />
          )
        }
        if (columnTemplate.filterOptions.length === 0) {
          return (
            <EmptyState hideBorder size="sm">
              <EmptyState.Image />
              <EmptyState.Message>No options found</EmptyState.Message>
            </EmptyState>
          )
        }
        if (!isNil(filter.isNull)) {
          return null
        }

        // When options span multiple modules, group by module with section
        // headers so duplicate status names (e.g. "Complete") stay readable.
        // Module-scoped views only ever load one module's statuses — skip grouping.
        const isModuleGroupedFilter =
          columnTemplate.dataType === 'RecordTypeStatus' &&
          recordTypeStatusOptionsSpanMultipleModules(columnTemplate.filterOptions)

        const sortedOptions = computed(() => {
          if (isModuleGroupedFilter) {
            return columnTemplate.filterOptions
          }
          return columnTemplate.filterOptions
            .slice()
            .sort((a, b) =>
              filter
                .getOptionDisplayValue(a.identifier, paymentProviderFormatted)
                .localeCompare(filter.getOptionDisplayValue(b.identifier, paymentProviderFormatted))
            )
        }).get()
        const activeOptions = sortedOptions.filter((o) => !o.isArchived)
        const archivedOptions = sortedOptions.filter((o) => o.isArchived)
        const showArchivedOptions =
          isArchivedOptionsListOpen ||
          // automatically show archived options if any is selected
          archivedOptions.some((o) => !!filter.optionsSelected?.includes(o.identifier))

        const renderGroupedOption = (option: ViewFilterOptionFragment) => {
          const statusName =
            option.data.__typename === 'ViewDataElementRecordTypeStatus'
              ? (option.data.recordTypeStatus?.name ?? '')
              : undefined
          return (
            <OptionCheckbox
              key={option.identifier}
              option={option}
              filter={filter}
              overrideLabel={statusName}
            />
          )
        }

        const renderMembershipOptions = (
          options: ViewFilterOptionFragment[],
          { applyFirstGroupMargin }: { applyFirstGroupMargin: boolean }
        ) => {
          if (!isModuleGroupedFilter) {
            return options.map((o) => (
              <OptionCheckbox key={o.identifier} option={o} filter={filter} />
            ))
          }
          return renderGroupedSections(options, renderGroupedOption, {
            applyFirstGroupMargin,
          })
        }

        return (
          <>
            {renderMembershipOptions(activeOptions, {
              applyFirstGroupMargin: true,
            })}
            {archivedOptions.length > 0 && (
              <>
                {!showArchivedOptions && (
                  <T.BodySmall>
                    <Link onClick={openArchivedOptionsList}>
                      {`Show archived options (${archivedOptions.length})`}
                    </Link>
                  </T.BodySmall>
                )}
                {showArchivedOptions && (
                  <>
                    <T.BodySmall className="mt-2 text-neutral-600">Archived Options</T.BodySmall>
                    {renderMembershipOptions(archivedOptions, {
                      applyFirstGroupMargin: false,
                    })}
                  </>
                )}
              </>
            )}
          </>
        )
      }
    }
  }

  return (
    <StyledFlex vertical gap="6px">
      <Flex gap="6px">
        <Text colorToken="colorTextSecondary">{columnTemplate.labelSentenceCase}</Text>
        <FilterTypeSelect filterManager={filterManager} columnTemplate={columnTemplate} />
      </Flex>
      {renderInputs()}
    </StyledFlex>
  )
}

export default observer(FilterEditor)
